import { PropsWithChildren, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';

import {
  LoggedType,
  useUserAuthContext,
} from '@/components/AuthTokenContextProvider/UserAuthContext.js';
import { useApolloClientContext } from '@/components/CustomApolloProvider/CustomApolloProvider.js';
import { useNotInMeetingContext } from '@/features/meeting/context/CurrentMeetingDataContext.js';
import { useAppVersion } from '@/hooks/useAppVersion.js';
import useDeviceSize from '@/hooks/useDeviceSize.js';
import { ROUTES } from '@/routes/Routes.js';

import EmptyPageWithTitle from '../page404/EmptyPageWithTitle.tsx.js';

/*
NOTE: This component contains code that should run on any page just like
the layout in nextjs. This is a good place to put global guards
*/
export const GlobalGuard = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const { isMobile } = useDeviceSize();
  const { apolloClient } = useApolloClientContext();

  const location = useLocation();
  const authContext = useUserAuthContext();
  const { currentMeetingId, setCurrentMeetingId } = useNotInMeetingContext();
  const nonLoadingStatusRef = useRef<LoggedType | null>(null);

  const isMeetingStatsRoute = matchPath(
    { path: ROUTES.MEETING_STATS, end: false },
    location.pathname
  );

  const isLiveMeetingRoute = matchPath(
    { path: ROUTES.LIVE_MEETING, end: false },
    location.pathname
  );

  const isPreviewRoute = matchPath({ path: ROUTES.PREVIEW_CASE, end: false }, location.pathname);

  useAppVersion();

  // NOTE: we need to refetch queries and subscriptions when the user passes from logged out to logged in and vice versa
  useEffect(() => {
    const current = authContext.status;
    const previous = nonLoadingStatusRef.current;

    const isTransitionBetweenLoggedInOut =
      (previous === LoggedType.LOGGED_OUT && current === LoggedType.LOGGED_IN) ||
      (previous === LoggedType.LOGGED_IN && current === LoggedType.LOGGED_OUT);

    if (isTransitionBetweenLoggedInOut) {
      console.log(
        'DEBUG >> GlobalGuard authContext changed between logged-in ↔ logged-out: Refetching',
        previous,
        current
      );
      void apolloClient.refetchQueries({
        include: 'active',
      });
    }

    if (current === LoggedType.LOGGED_IN || current === LoggedType.LOGGED_OUT) {
      nonLoadingStatusRef.current = current;
    }
  }, [authContext]);

  // NOTE: when the user is not on either a live meeting or a meeting stats route,
  // we need to reset the currentMeetingId to null
  useEffect(() => {
    if (!isLiveMeetingRoute && !isPreviewRoute && currentMeetingId) {
      console.log('DEBUG >> GlobalGuard: Resetting currentMeetingId to null');
      setCurrentMeetingId(null);
    }
  }, [isLiveMeetingRoute, isPreviewRoute, currentMeetingId, setCurrentMeetingId]);

  if (isMobile && !isMeetingStatsRoute) {
    return <EmptyPageWithTitle title={t('mobileview.title')} subtitle="" />;
  }
  return children;
};
